import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import { usePromptApi } from '../Services/PromptApiProvider';
import { PromptIssue } from '../Services/PromptApiProviderTypes';
import './PromptAnalyzerModal.css';
import {
  buildHighlightedParts,
  copyToClipboard,
  flashElement,
  IssueCardProps,
  PromptAnalyzerModalProps,
  scrollToElement,
  SEVERITY_ORDER,
  TYPE_ICONS,
  TYPE_LABELS,
} from './PromptAnalyzerModalHelpers';
import { analyzerReducer, initialAnalyzerState } from './PromptAnalyzerModalReducer';

// --- Sub-components ---

function Spinner() {
  return (
    <svg
      className="pa-spinner"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
    >
      <path d="M12 2 a10 10 0 0 1 10 10" strokeLinecap="round" />
    </svg>
  );
}

function IssueCard({ issue, isActive, isFixed, onClick, cardRef }: IssueCardProps) {
  return (
    <div
      ref={cardRef}
      className={
        `pa-issue-card sev-${issue.severity}` +
        (isActive ? ' pa-issue-card--active' : '') +
        (isFixed ? ' pa-issue-card--fixed' : '')
      }
      onClick={() => onClick(issue.id)}
    >
      <div className="pa-issue-card__header">
        <span className="pa-issue-type-badge">
          <span className="pa-issue-type-icon">{TYPE_ICONS[issue.type] || '•'}</span>
          {TYPE_LABELS[issue.type] || issue.type}
        </span>
        <span className={`pa-sev-badge sev-${issue.severity}`}>{issue.severity}</span>
        {isFixed && <span className="pa-fixed-badge">✓ Fixed</span>}
      </div>
      <p className="pa-issue-explanation">{issue.explanation}</p>
      {isActive && (
        <>
          {issue.fix && (
            <div className="pa-issue-fix">
              <span className="pa-fix-label">Recommended fix</span>
              <p>{issue.fix}</p>
            </div>
          )}
          {issue.segments?.length > 0 && (
            <div className="pa-issue-segments">
              {issue.segments.map((seg, i) => (
                <pre key={i} className="pa-segment-quote">
                  {seg}
                </pre>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// --- Main component ---

export default function PromptAnalyzerModal({
  promptText,
  patternType,
  isOpen,
  onClose,
  onApply,
}: PromptAnalyzerModalProps) {
  const { analyzePrompt, rewritePrompt } = usePromptApi();
  const isMultiField = patternType && patternType !== 'simple_prompt';

  const [state, dispatch] = useReducer(analyzerReducer, initialAnalyzerState);
  const {
    editableText,
    issues,
    summary,
    activeIssueId,
    isEditing,
    analyzing,
    rewriting,
    hasAnalyzed,
    rewrittenText,
    changeSummary,
    error,
    promptTab,
    copied,
  } = state;

  const lastSyncedPrompt = useRef<string | null>(null);
  const markRefs = useRef<Record<string, HTMLElement | null>>({});
  const cardRefs = useRef<Record<string, HTMLElement | null>>({});
  const highlightRef = useRef<HTMLDivElement | null>(null);
  const issuesListRef = useRef<HTMLDivElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const savedScrollTop = useRef<number>(0);

  // --- Effects ---

  useEffect(() => {
    if (isOpen && promptText !== lastSyncedPrompt.current) {
      lastSyncedPrompt.current = promptText;
      dispatch({ type: 'RESET', promptText: promptText || '' });
    }
  }, [isOpen, promptText]);

  useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.focus({ preventScroll: true });
    }
  }, [isEditing]);

  useEffect(() => {
    requestAnimationFrame(() => {
      if (isEditing && textareaRef.current) {
        textareaRef.current.scrollTop = savedScrollTop.current;
      } else if (!isEditing && highlightRef.current) {
        highlightRef.current.scrollTop = savedScrollTop.current;
      }
    });
  }, [isEditing]);

  // --- Derived ---

  const counts = useMemo(() => {
    const c = { high: 0, medium: 0, low: 0 };
    issues.forEach((i) => c[i.severity]++);
    return c;
  }, [issues]);

  const highlightedParts = useMemo(
    () => buildHighlightedParts(editableText, issues),
    [editableText, issues],
  );

  const hasRewrite = !!rewrittenText;
  const showingOriginal = promptTab === 'original';

  // --- Handlers ---

  const scrollToMark = useCallback((issueId: string) => {
    const el = markRefs.current[issueId];
    scrollToElement(
      el,
      highlightRef.current,
      highlightRef.current?.clientHeight ? highlightRef.current.clientHeight / 3 : 0,
    );
    flashElement(el, 'pa-mark--flash');
  }, []);

  const scrollToCard = useCallback((issueId: string) => {
    scrollToElement(cardRefs.current[issueId], issuesListRef.current, 12);
  }, []);

  const handleAnalyze = useCallback(async () => {
    if (!editableText.trim()) return;
    dispatch({ type: 'ANALYZE_START' });

    try {
      const res = await analyzePrompt(editableText);
      const sortedIssues = (res.issues || []).sort(
        (a: PromptIssue, b: PromptIssue) =>
          (SEVERITY_ORDER[a.severity] ?? 99) - (SEVERITY_ORDER[b.severity] ?? 99),
      );
      dispatch({ type: 'ANALYZE_SUCCESS', issues: sortedIssues, summary: res.summary || '' });
    } catch (e) {
      console.error('Analyze error:', e);
      dispatch({ type: 'ANALYZE_ERROR', error: 'Analysis failed. Please try again.' });
    }
  }, [editableText, analyzePrompt]);

  const handleRewrite = useCallback(async () => {
    if (issues.length === 0) return;
    dispatch({ type: 'REWRITE_START' });

    try {
      const res = await rewritePrompt(editableText, issues);
      dispatch({
        type: 'REWRITE_SUCCESS',
        optimizedPrompt: res.optimized_prompt || '',
        changeSummary: res.change_summary || '',
      });
    } catch (e) {
      console.error('Rewrite error:', e);
      dispatch({ type: 'REWRITE_ERROR', error: 'Rewrite failed. Please try again.' });
    }
  }, [editableText, issues, rewritePrompt]);

  const handleAcceptRewrite = useCallback(() => {
    dispatch({ type: 'ACCEPT_REWRITE', rewrittenText });
  }, [rewrittenText]);

  const handleApply = useCallback(() => {
    const textToApply = promptTab === 'rewritten' && rewrittenText ? rewrittenText : editableText;
    onApply?.(textToApply);
    lastSyncedPrompt.current = textToApply;
    onClose();
  }, [promptTab, rewrittenText, editableText, onApply, onClose]);

  const handleIssueCardClick = useCallback(
    (id: string) => {
      const next = activeIssueId === id ? null : id;
      dispatch({ type: 'SET_ACTIVE_ISSUE', id: next });
      if (next) {
        if (promptTab !== 'original') dispatch({ type: 'SET_PROMPT_TAB', tab: 'original' });
        setTimeout(() => scrollToMark(next), 80);
      }
    },
    [activeIssueId, promptTab, scrollToMark],
  );

  const handleMarkClick = useCallback(
    (id: string) => {
      const next = activeIssueId === id ? null : id;
      dispatch({ type: 'SET_ACTIVE_ISSUE', id: next });
      if (next) setTimeout(() => scrollToCard(next), 80);
    },
    [activeIssueId, scrollToCard],
  );

  const toggleEdit = useCallback(() => {
    if (isEditing && textareaRef.current) {
      savedScrollTop.current = textareaRef.current.scrollTop;
    } else if (highlightRef.current) {
      savedScrollTop.current = highlightRef.current.scrollTop;
    }
    dispatch({ type: 'TOGGLE_EDIT' });
  }, [isEditing]);

  const handleCopyRewrite = useCallback(async () => {
    if (!rewrittenText) return;
    await copyToClipboard(rewrittenText);
    dispatch({ type: 'SET_COPIED', copied: true });
    setTimeout(() => dispatch({ type: 'SET_COPIED', copied: false }), 2000);
  }, [rewrittenText]);

  // --- Render ---

  if (!isOpen) return null;

  return (
    <div className="pa-overlay" onClick={onClose}>
      <div className="pa-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="pa-header">
          <div className="pa-header__left">
            <span className="pa-header__icon">✦</span>
            <span className="pa-header__title">Prompt Analyzer</span>
          </div>
          <div className="pa-header__right">
            {hasAnalyzed && issues.length > 0 && (
              <div className="pa-header__count">
                {counts.high > 0 && (
                  <span className="pa-count-dot sev-high">{counts.high} high</span>
                )}
                {counts.medium > 0 && (
                  <span className="pa-count-dot sev-medium">{counts.medium} med</span>
                )}
                {counts.low > 0 && <span className="pa-count-dot sev-low">{counts.low} low</span>}
              </div>
            )}
            {hasRewrite && <span className="pa-header__rewritten-badge">✓ Rewritten</span>}
            <button className="pa-close-btn" onClick={onClose} title="Close">
              ✕
            </button>
          </div>
        </div>

        {/* Summary */}
        {summary && showingOriginal && (
          <div className={`pa-summary-bar ${issues.length === 0 ? 'pa-summary-bar--success' : ''}`}>
            <span className="pa-summary-icon">{issues.length === 0 ? '✓' : 'ℹ'}</span>
            <span>{summary}</span>
          </div>
        )}
        {!showingOriginal && changeSummary && (
          <div className="pa-summary-bar pa-summary-bar--success">
            <span className="pa-summary-icon">✓</span>
            <span>{changeSummary}</span>
          </div>
        )}

        {/* Body */}
        <div className="pa-body">
          {/* Prompt panel */}
          <div className="pa-prompt-panel">
            <div className="pa-panel-header">
              <div className="pa-prompt-tabs">
                <button
                  className={`pa-prompt-tab ${showingOriginal ? 'pa-prompt-tab--active' : ''}`}
                  onClick={() => dispatch({ type: 'SET_PROMPT_TAB', tab: 'original' })}
                >
                  Original
                </button>
                <button
                  className={
                    `pa-prompt-tab` +
                    (!showingOriginal ? ' pa-prompt-tab--active' : '') +
                    (!hasRewrite ? ' pa-prompt-tab--disabled' : '')
                  }
                  onClick={() =>
                    hasRewrite && dispatch({ type: 'SET_PROMPT_TAB', tab: 'rewritten' })
                  }
                  disabled={!hasRewrite}
                >
                  Rewritten
                  {hasRewrite && <span className="pa-tab-dot" />}
                </button>
              </div>
              {showingOriginal && !isMultiField && (
                <button className="pa-toggle-edit-btn" onClick={toggleEdit}>
                  {isEditing ? '👁 Preview' : '✎ Edit'}
                </button>
              )}
              {showingOriginal && isMultiField && (
                <span className="pa-multifield-label">
                  {patternType?.replace(/_/g, ' ').toUpperCase()}
                </span>
              )}
            </div>

            <div className="pa-prompt-area">
              {(analyzing || rewriting) && (
                <div className="pa-loading-overlay">
                  <Spinner />
                  <span>{analyzing ? 'Analyzing prompt…' : 'Rewriting prompt…'}</span>
                </div>
              )}

              {showingOriginal && (
                <>
                  {isEditing ? (
                    <textarea
                      ref={textareaRef}
                      className="pa-textarea"
                      value={editableText}
                      onChange={(e) =>
                        dispatch({ type: 'SET_EDITABLE_TEXT', text: e.target.value })
                      }
                      placeholder="Paste or type your prompt here…"
                      spellCheck={false}
                    />
                  ) : (
                    <div className="pa-highlight-view" ref={highlightRef}>
                      <pre className="pa-pre">
                        {highlightedParts.map((part, idx) =>
                          part.issueId ? (
                            <mark
                              key={idx}
                              ref={(el) => {
                                if (el) markRefs.current[part.issueId!] = el;
                              }}
                              className={
                                `pa-mark pa-mark--${part.severity}` +
                                (activeIssueId === part.issueId ? ' pa-mark--active' : '')
                              }
                              onClick={() => handleMarkClick(part.issueId!)}
                              title={`Issue: ${part.issueId}`}
                            >
                              {part.text}
                            </mark>
                          ) : (
                            <span key={idx}>{part.text}</span>
                          ),
                        )}
                      </pre>
                    </div>
                  )}
                </>
              )}

              {!showingOriginal && (
                <div className="pa-highlight-view">
                  <pre className="pa-pre">{rewrittenText}</pre>
                </div>
              )}
            </div>
          </div>

          <div className="pa-divider" />

          {/* Issues panel */}
          <div className="pa-issues-panel">
            <div className="pa-panel-header">
              <span className="pa-panel-title">
                Issues {hasAnalyzed && issues.length > 0 ? `(${issues.length})` : ''}
              </span>
            </div>
            <div className="pa-issues-list" ref={issuesListRef}>
              {!hasAnalyzed && !analyzing && (
                <div className="pa-placeholder">
                  <span className="pa-placeholder-icon">🔍</span>
                  <span>
                    Click <strong>Analyze</strong> to detect issues in your prompt
                  </span>
                </div>
              )}
              {hasAnalyzed && issues.length === 0 && (
                <div className="pa-placeholder pa-placeholder--success">
                  <span className="pa-placeholder-icon">✅</span>
                  <span>No issues detected — your prompt looks good!</span>
                </div>
              )}
              {issues.map((issue) => (
                <IssueCard
                  key={issue.id}
                  issue={issue}
                  isActive={activeIssueId === issue.id}
                  isFixed={hasRewrite && !showingOriginal}
                  onClick={handleIssueCardClick}
                  cardRef={(el) => {
                    if (el) cardRefs.current[issue.id] = el;
                  }}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="pa-footer">
          {error && <span className="pa-error-msg">{error}</span>}
          {isMultiField && hasRewrite && (
            <span className="pa-multifield-hint">
              ℹ Multi-field pattern — copy the rewrite and update fields manually
            </span>
          )}
          <div className="pa-footer__actions">
            <button
              className="pa-btn pa-btn--analyze"
              disabled={analyzing || !editableText.trim()}
              onClick={handleAnalyze}
            >
              {analyzing ? (
                <>
                  <Spinner /> Analyzing…
                </>
              ) : (
                '🔍 Analyze'
              )}
            </button>
            {hasAnalyzed && issues.length > 0 && (
              <button
                className="pa-btn pa-btn--rewrite"
                disabled={rewriting}
                onClick={handleRewrite}
              >
                {rewriting ? (
                  <>
                    <Spinner /> Rewriting…
                  </>
                ) : (
                  '✦ Rewrite'
                )}
              </button>
            )}
            {!isMultiField && hasRewrite && (
              <button
                className="pa-btn pa-btn--accept"
                onClick={handleAcceptRewrite}
                title="Replace original with rewritten version"
              >
                ✓ Accept rewrite
              </button>
            )}
            {!isMultiField && (
              <button
                className="pa-btn pa-btn--apply"
                disabled={!editableText.trim()}
                onClick={handleApply}
              >
                Apply & Close
              </button>
            )}
            {isMultiField && hasRewrite && (
              <button className="pa-btn pa-btn--copy" onClick={handleCopyRewrite}>
                {copied ? '✓ Copied!' : '📋 Copy rewrite'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
